export interface Address {
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  zipCode: string;
  zipPlusFour?: string;
  state: string;
}

/**
 * US state codes and labels for use in address forms.
 * Each entry contains a two-letter state code and full state name.
 */
export const US_STATES = [
  { value: "AK", label: "Alaska" },
  { value: "AL", label: "Alabama" },
  { value: "AR", label: "Arkansas" },
  { value: "AS", label: "American Samoa" },
  { value: "AZ", label: "Arizona" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DC", label: "Washington DC" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "GU", label: "Guam" },
  { value: "HI", label: "Hawaii" },
  { value: "IA", label: "Iowa" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "MA", label: "Massachusetts" },
  { value: "MD", label: "Maryland" },
  { value: "ME", label: "Maine" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MO", label: "Missouri" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "MS", label: "Mississippi" },
  { value: "MT", label: "Montana" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "NE", label: "Nebraska" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NV", label: "Nevada" },
  { value: "NY", label: "New York" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "PR", label: "Puerto Rico" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UM", label: "United States Minor Outlying Islands" },
  { value: "UT", label: "Utah" },
  { value: "VA", label: "Virginia" },
  { value: "VI", label: "Virgin Islands" },
  { value: "VT", label: "Vermont" },
  { value: "WA", label: "Washington" },
  { value: "WI", label: "Wisconsin" },
  { value: "WV", label: "West Virginia" },
  { value: "WY", label: "Wyoming" },
];

export type USStateCode = (typeof US_STATES)[number]["value"];

/**
 * Formats a US address into a standardized string representation.
 *
 * @template T - Type extending the Address interface
 * @param {T} address - The address object to format
 * @param {boolean} [multiLine=false] - Whether to format the address across multiple lines
 *
 * @returns {string} The formatted address string
 *
 * @remarks
 * - Single-line format: "123 Main St, Apt 4B, Springfield, IL 62701-1234"
 * - Multi-line format uses newline characters (\n) to separate lines
 * - ZIP+4 is appended if provided (format: ZIP-PLUS4)
 * - Address line two is optional and omitted if not provided
 *
 * @example
 * ```typescript
 * const address = {
 *   addressLineOne: "123 Main St",
 *   addressLineTwo: "Apt 4B",
 *   city: "Springfield",
 *   state: "IL",
 *   zipCode: "62701",
 *   zipPlusFour: "1234"
 * };
 *
 * // Single-line format
 * formatAddress(address);
 * // Returns: "123 Main St, Apt 4B, Springfield, IL 62701-1234"
 *
 * // Multi-line format
 * formatAddress(address, true);
 * // Returns:
 * // "123 Main St
 * // Apt 4B
 * // Springfield, IL 62701-1234"
 *
 * // Without address line two
 * formatAddress({ ...address, addressLineTwo: undefined });
 * // Returns: "123 Main St, Springfield, IL 62701-1234"
 *
 * // Without ZIP+4
 * formatAddress({ ...address, zipPlusFour: undefined });
 * // Returns: "123 Main St, Apt 4B, Springfield, IL 62701"
 * ```
 */
export const formatAddress = <T extends Address>(address: T, multiLine = false): string => {
  const { addressLineOne, addressLineTwo, city, state, zipCode, zipPlusFour } = address;

  // Format ZIP code with ZIP+4 if available
  const formattedZip = zipPlusFour ? `${zipCode}-${zipPlusFour}` : zipCode;

  // Format city, state, ZIP line
  const cityStateZip = city || state || formattedZip ? `${city}, ${state} ${formattedZip}` : "";

  if (multiLine) {
    // Multi-line format
    const lines = [addressLineOne];
    if (addressLineTwo) {
      lines.push(addressLineTwo);
    }
    lines.push(cityStateZip);
    return lines.filter(Boolean).join("\n");
  } else {
    // Single-line format
    const parts = [addressLineOne];
    if (addressLineTwo) {
      parts.push(addressLineTwo);
    }
    parts.push(cityStateZip);
    return parts.filter(Boolean).join(", ");
  }
};
